import { getCurrentDemoClientId } from 'tio-employee/utils/career-pathways-demo-data';
import type {
  FinancialSnapshot,
  LrapDiscoverySummary,
  LrapProgram,
  PslfAchievement,
  PslfAutomationStatus,
  PslfJourney,
  PslfPaymentValidation,
  PslfProgress,
  PslfProjection,
  PslfProtectionStatus,
  PslfScenario,
  PslfVaultDocument,
  PslfVaultSummary,
  Section127Recommendation,
  StackingOptimization,
} from './types';

export function getFinancialSnapshotForClient(): FinancialSnapshot {
  const clientId = getCurrentDemoClientId();

  const baseSnapshot: FinancialSnapshot = {
    section127: {
      annualLimit: 5250,
      yearToDateUsed: 1850,
      remaining: 3400,
      loanAllocation: 1200,
      tuitionAllocation: 650,
      strategy: 'balanced',
      updatedAt: '2025-01-15T10:30:00Z',
    },
    studentLoans: {
      totalBalance: 42500,
      monthlyPayment: 385,
      interestRate: 6.5,
      estimatedPayoffDate: '2034-08-15',
      servicer: 'MOHELA',
      paymentsMade: 36,
    },
    employerContributions: {
      yearToDate: 1850,
      lifetime: 8750,
      projectedAnnual: 5250,
    },
    optimizationPotential: {
      potentialSavings: 2400,
      projectedPayoffAcceleration: '2.5 years',
      taxSavingsYtd: 462.5,
    },
  };

  if (clientId === 'ups') {
    return {
      ...baseSnapshot,
      section127: {
        ...baseSnapshot.section127,
        annualLimit: 3500,
        yearToDateUsed: 900,
        remaining: 2600,
        loanAllocation: 600,
        tuitionAllocation: 300,
      },
      studentLoans: {
        totalBalance: 28000,
        monthlyPayment: 285,
        interestRate: 5.8,
        estimatedPayoffDate: '2032-03-15',
        servicer: 'Nelnet',
        paymentsMade: 48,
      },
      employerContributions: {
        yearToDate: 900,
        lifetime: 5600,
        projectedAnnual: 3500,
      },
      optimizationPotential: {
        potentialSavings: 1800,
        projectedPayoffAcceleration: '1.8 years',
        taxSavingsYtd: 225.0,
      },
    };
  }

  if (clientId === 'starbucks') {
    return {
      ...baseSnapshot,
      section127: {
        ...baseSnapshot.section127,
        yearToDateUsed: 2400,
        remaining: 2850,
        loanAllocation: 1500,
        tuitionAllocation: 900,
        strategy: 'loan-focused',
      },
      studentLoans: {
        totalBalance: 35000,
        monthlyPayment: 320,
        interestRate: 6.2,
        estimatedPayoffDate: '2033-06-01',
        servicer: 'FedLoan',
        paymentsMade: 24,
      },
      employerContributions: {
        yearToDate: 2400,
        lifetime: 7200,
        projectedAnnual: 5250,
      },
      optimizationPotential: {
        potentialSavings: 3200,
        projectedPayoffAcceleration: '2.8 years',
        taxSavingsYtd: 600.0,
      },
    };
  }

  return baseSnapshot;
}

export function getSection127RecommendationForClient(): Section127Recommendation {
  const clientId = getCurrentDemoClientId();
  const snapshot = getFinancialSnapshotForClient();

  const baseRecommendation: Section127Recommendation = {
    recommendedStrategy: 'balanced',
    recommendedLoanAllocation: 2500,
    recommendedTuitionAllocation: 2750,
    reasoning: `Based on your loan balance of $${snapshot.studentLoans.totalBalance.toLocaleString()} at ${snapshot.studentLoans.interestRate}% interest, a balanced approach maximizes both career growth and debt reduction.`,
    factors: [
      {
        name: 'Interest Rate',
        impact: 'high',
        description: `Your ${snapshot.studentLoans.interestRate}% rate is above the optimal threshold for prioritizing tuition.`,
      },
      {
        name: 'Career Stage',
        impact: 'medium',
        description: 'Early-to-mid career employees benefit from continued education investment.',
      },
      {
        name: 'Loan Balance',
        impact: 'high',
        description: `With $${snapshot.studentLoans.totalBalance.toLocaleString()} remaining, accelerating payoff provides significant long-term savings.`,
      },
    ],
    projectedImpact: {
      scenario: 'recommended',
      loanPayoffDate: '2032-01-15',
      totalInterestSaved: 8500,
      taxSavings: 1312.5,
      careerAdvancementScore: 85,
    },
    alternativeScenarios: [
      {
        scenario: 'loan-focused',
        loanPayoffDate: '2030-06-01',
        totalInterestSaved: 12800,
        taxSavings: 1312.5,
        careerAdvancementScore: 65,
      },
      {
        scenario: 'tuition-focused',
        loanPayoffDate: '2034-08-15',
        totalInterestSaved: 2100,
        taxSavings: 1312.5,
        careerAdvancementScore: 95,
      },
    ],
    actions: [
      {
        id: 'action-1',
        title: 'Adjust allocation to balanced strategy',
        description: 'Split your Section 127 benefit equally between loan payments and tuition.',
        impact: 'Saves $8,500 in interest while maintaining career growth',
        priority: 'high',
      },
      {
        id: 'action-2',
        title: 'Enroll in recommended program',
        description:
          'Start the Healthcare Leadership Certificate to boost career advancement score.',
        impact: 'Increases promotion readiness by 15%',
        priority: 'medium',
      },
    ],
    lastUpdated: new Date().toISOString(),
  };

  if (clientId === 'ups') {
    return {
      ...baseRecommendation,
      recommendedStrategy: 'loan-focused',
      recommendedLoanAllocation: 2500,
      recommendedTuitionAllocation: 1000,
      reasoning:
        'With your moderate loan balance and lower interest rate, focusing on debt reduction while maintaining some education investment is optimal.',
    };
  }

  if (clientId === 'starbucks') {
    return {
      ...baseRecommendation,
      recommendedStrategy: 'tuition-focused',
      recommendedLoanAllocation: 1500,
      recommendedTuitionAllocation: 3750,
      reasoning:
        'Your ASU partnership provides exceptional tuition coverage. Maximizing education investment accelerates your path to Store Manager.',
    };
  }

  return baseRecommendation;
}
export const MOCK_LRAP_PROGRAMS: LrapProgram[] = [
  {
    id: 'lrap-georgetown',
    name: 'Georgetown LRAP III',
    type: 'school',
    sponsor: 'Georgetown University Law Center',
    description:
      'Covers 100% of Direct Loan payments for graduates in public interest work earning under $75,000.',
    maxAnnual: 12000,
    maxLifetime: null,
    eligibilityRequirements: [
      'Georgetown Law graduate',
      'Public interest employment',
      'Salary under $75,000',
      'Enrolled in IDR plan',
    ],
    applicationDeadline: '2026-03-15',
    applicationUrl: 'https://www.law.georgetown.edu/admissions-aid/financial-aid/lrap/',
    stackingNotes: 'Can be combined with PSLF. May reduce based on outside funds.',
    incompatibleWith: [],
    outsideFundsPolicy: 'reduces',
  },
  {
    id: 'lrap-nyu',
    name: 'NYU LRAP Plus',
    type: 'school',
    sponsor: 'NYU School of Law',
    description:
      'Income-driven assistance covering 100% of IDR payments for qualifying public interest positions.',
    maxAnnual: 15000,
    maxLifetime: null,
    eligibilityRequirements: [
      'NYU Law graduate',
      'Qualifying public interest work',
      'Income under $110,000',
    ],
    applicationDeadline: '2026-04-01',
    applicationUrl: 'https://www.law.nyu.edu/financialaid/lrap',
    stackingNotes: 'Highly integrated with PSLF tracking.',
    incompatibleWith: [],
    outsideFundsPolicy: 'reduces',
  },
  {
    id: 'lrap-florida-bar',
    name: 'Florida Bar Foundation LRAP',
    type: 'state',
    sponsor: 'The Florida Bar Foundation',
    description:
      'Loan repayment assistance for attorneys providing civil legal aid to low-income Floridians.',
    maxAnnual: 5000,
    maxLifetime: 25000,
    eligibilityRequirements: [
      'Licensed Florida attorney',
      'Employed by eligible legal aid organization',
      'Salary under $65,000',
    ],
    applicationDeadline: '2026-05-15',
    applicationUrl: 'https://thefloridabarfoundation.org/lrap',
    stackingNotes: 'Can be combined with employer contributions.',
    incompatibleWith: [],
    outsideFundsPolicy: 'no-impact',
  },
  {
    id: 'lrap-maryland-hoffman',
    name: 'Janet L. Hoffman LRAP (Maryland)',
    type: 'state',
    sponsor: 'Maryland Higher Education Commission',
    description:
      'Loan assistance for Maryland residents working in state or local government or nonprofit organizations.',
    maxAnnual: 7500,
    maxLifetime: 30000,
    eligibilityRequirements: [
      'Maryland resident',
      'State/local government or nonprofit employee',
      'Minimum 2-year service commitment',
    ],
    applicationDeadline: '2026-06-01',
    applicationUrl: 'https://mhec.maryland.gov/preparing/Pages/FinancialAid/descriptions.aspx',
    stackingNotes: 'Compatible with federal PSLF program.',
    incompatibleWith: [],
    outsideFundsPolicy: 'no-impact',
  },
  {
    id: 'lrap-doj',
    name: 'DOJ Attorney Student Loan Repayment Program',
    type: 'federal',
    sponsor: 'U.S. Department of Justice',
    description:
      'Federal agency LRAP for DOJ attorneys under 5 U.S.C. 5379, up to $10,000/year with 3-year commitment.',
    maxAnnual: 10000,
    maxLifetime: 60000,
    eligibilityRequirements: [
      'DOJ attorney employee',
      '3-year service agreement',
      'Federal student loans only',
    ],
    applicationDeadline: null,
    applicationUrl: 'https://www.justice.gov/legal-careers',
    stackingNotes: 'Counts toward PSLF qualifying payments.',
    incompatibleWith: [],
    outsideFundsPolicy: 'no-impact',
  },
  {
    id: 'lrap-nhsc',
    name: 'National Health Service Corps (NHSC)',
    type: 'federal',
    sponsor: 'Health Resources & Services Administration',
    description:
      'Up to $75,000 for 2 years of service in Health Professional Shortage Areas (HPSAs).',
    maxAnnual: 37500,
    maxLifetime: 75000,
    eligibilityRequirements: [
      'Primary care clinician',
      'Work in HPSA-designated area',
      '2-year minimum service commitment',
    ],
    applicationDeadline: '2026-02-28',
    applicationUrl: 'https://nhsc.hrsa.gov/loan-repayment',
    stackingNotes: 'Cannot combine with certain other federal programs.',
    incompatibleWith: ['lrap-nurse-corps'],
    outsideFundsPolicy: 'no-impact',
  },
];

export function getLrapDiscoveryForClient(): LrapDiscoverySummary {
  const clientId = getCurrentDemoClientId() as string;

  // Law firm scenario - show school LRAP conflict
  if (clientId === 'legal') {
    return {
      hasCompletedDiscovery: true,
      discoveryDate: '2025-12-01',
      enrolledPrograms: [
        {
          id: 'enrollment-1',
          programId: 'lrap-georgetown',
          programName: 'Georgetown LRAP III',
          programType: 'school',
          enrolledDate: '2024-08-15',
          annualBenefit: 8400,
          ytdReceived: 4200,
          lifetimeReceived: 12600,
          nextPaymentDate: '2026-02-15',
          status: 'active',
          expirationDate: null,
        },
      ],
      eligiblePrograms: [
        {
          programId: 'lrap-florida-bar',
          program: MOCK_LRAP_PROGRAMS.find((p) => p.id === 'lrap-florida-bar')!,
          status: 'likely-eligible',
          estimatedAnnualValue: 5000,
          matchedCriteria: ['Licensed attorney', 'Legal aid work'],
          missingCriteria: ['Florida residency verification'],
          conflictWarnings: [],
          nextSteps: ['Verify Florida bar status', 'Submit application by May 15'],
        },
      ],
      totalPotentialAnnualValue: 13400,
      outsideFundsConflicts: [
        {
          schoolProgram: 'Georgetown LRAP III',
          employerContribution: 5250,
          potentialReduction: 5250,
          recommendation:
            'Your Georgetown LRAP has an "outside funds" policy. Consider routing employer contribution to private bar study loan or SECURE 2.0 match instead.',
        },
      ],
      stackingOptimization: getStackingOptimizationForClient(),
    };
  }

  // Starbucks scenario - no LRAP programs identified yet
  if (clientId === 'starbucks') {
    return {
      hasCompletedDiscovery: false,
      discoveryDate: null,
      enrolledPrograms: [],
      eligiblePrograms: [],
      totalPotentialAnnualValue: 0,
      outsideFundsConflicts: [],
      stackingOptimization: null,
    };
  }

  // Healthcare/Advocate scenario - NHSC and state nursing programs
  if (clientId === 'advocate') {
    return {
      hasCompletedDiscovery: true,
      discoveryDate: '2025-10-15',
      enrolledPrograms: [],
      eligiblePrograms: [
        {
          programId: 'lrap-nhsc',
          program: MOCK_LRAP_PROGRAMS.find((p) => p.id === 'lrap-nhsc')!,
          status: 'eligible',
          estimatedAnnualValue: 37500,
          matchedCriteria: [
            'Primary care clinician',
            'Work in HPSA-designated area',
            '2-year service commitment available',
          ],
          missingCriteria: [],
          conflictWarnings: ['Cannot combine with Nurse Corps if awarded'],
          nextSteps: [
            'Submit NHSC application by Feb 28, 2026',
            'Obtain site verification from employer',
            'Complete HPSA eligibility check',
          ],
        },
        {
          programId: 'lrap-illinois-nurse',
          program: {
            id: 'lrap-illinois-nurse',
            name: 'Illinois Nurse Educator Loan Repayment',
            type: 'state',
            sponsor: 'Illinois Student Assistance Commission',
            description: 'Up to $5,000/year for nurses who commit to teaching nursing in Illinois.',
            maxAnnual: 5000,
            maxLifetime: 20000,
            eligibilityRequirements: [
              'Illinois licensed nurse',
              'Teaching at Illinois nursing program',
              '1-year service commitment per award year',
            ],
            applicationDeadline: '2026-04-15',
            applicationUrl: 'https://www.isac.org/students/during-college/loan-repayment/',
            stackingNotes: 'Compatible with NHSC and employer programs.',
            incompatibleWith: [],
            outsideFundsPolicy: 'no-impact',
          },
          status: 'likely-eligible',
          estimatedAnnualValue: 5000,
          matchedCriteria: ['Illinois licensed nurse', 'Clinical instructor role'],
          missingCriteria: ['Teaching commitment verification'],
          conflictWarnings: [],
          nextSteps: [
            'Confirm teaching responsibilities meet program requirements',
            'Submit application by April 15',
          ],
        },
      ],
      totalPotentialAnnualValue: 42500,
      outsideFundsConflicts: [],
      stackingOptimization: {
        totalAvailableAssistance: 47750,
        optimizedAllocation: [
          {
            source: 'NHSC',
            sourceType: 'federal',
            amount: 37500,
            targetDebt: 'Federal Direct Loans',
            rationale:
              'NHSC provides the largest benefit. Accept this first and build service commitment.',
          },
          {
            source: 'Employer Section 127',
            sourceType: 'employer',
            amount: 5250,
            targetDebt: 'Private Graduate Plus Loans',
            rationale: 'Use employer benefit for private loans not covered by NHSC.',
          },
          {
            source: 'Illinois Nurse Educator',
            sourceType: 'state',
            amount: 5000,
            targetDebt: 'Any remaining student loans',
            rationale: 'Stack state program on top of federal and employer benefits.',
          },
        ],
        reasoning:
          'As a healthcare professional in a shortage area, you qualify for exceptional NHSC benefits. We recommend accepting the federal program first ($75k over 2 years), supplementing with employer contributions to private loans, and adding state benefits.',
        projectedOutcome: {
          outOfPocketMonthly: 0,
          yearsToPayoff: 2.5,
          totalForgiven: 75000,
          retirementContribution: 0,
        },
      },
    };
  }

  return {
    hasCompletedDiscovery: true,
    discoveryDate: '2025-11-15',
    enrolledPrograms: [],
    eligiblePrograms: [
      {
        programId: 'lrap-maryland-hoffman',
        program: MOCK_LRAP_PROGRAMS.find((p) => p.id === 'lrap-maryland-hoffman')!,
        status: 'check-required',
        estimatedAnnualValue: 7500,
        matchedCriteria: ['Nonprofit employment'],
        missingCriteria: ['Maryland residency', 'Service commitment'],
        conflictWarnings: [],
        nextSteps: ['Verify Maryland residency', 'Review service commitment terms'],
      },
    ],
    totalPotentialAnnualValue: 7500,
    outsideFundsConflicts: [],
    stackingOptimization: null,
  };
}

export function getStackingOptimizationForClient(): StackingOptimization {
  const clientId = getCurrentDemoClientId() as string;

  if (clientId === 'legal') {
    return {
      totalAvailableAssistance: 18650,
      optimizedAllocation: [
        {
          source: 'Georgetown LRAP',
          sourceType: 'school',
          amount: 8400,
          targetDebt: 'Federal Direct Loans (IDR payment)',
          rationale: 'School covers 100% of your IDR payment. This counts toward PSLF.',
        },
        {
          source: 'Employer Section 127',
          sourceType: 'employer',
          amount: 3000,
          targetDebt: 'Private Bar Study Loan',
          rationale:
            'Not covered by Georgetown LRAP. Routing here avoids "outside funds" reduction.',
        },
        {
          source: 'Employer Section 127',
          sourceType: 'employer',
          amount: 2250,
          targetDebt: 'SECURE 2.0 401(k) Match',
          rationale:
            'Remaining benefit triggers retirement match. Builds wealth, not just debt payoff.',
        },
      ],
      reasoning:
        'Based on your Georgetown LRAP enrollment and PSLF eligibility, we recommend $0 to federal loans (school covers IDR), $3,000 to private bar loan, and $2,250 to 401(k) via SECURE 2.0.',
      projectedOutcome: {
        outOfPocketMonthly: 0,
        yearsToPayoff: 6.2,
        totalForgiven: 68000,
        retirementContribution: 4500,
      },
    };
  }

  return {
    totalAvailableAssistance: 5250,
    optimizedAllocation: [
      {
        source: 'Employer Section 127',
        sourceType: 'employer',
        amount: 2500,
        targetDebt: 'Federal Student Loans',
        rationale: 'Accelerate payoff of highest-interest federal loans.',
      },
      {
        source: 'Employer Section 127',
        sourceType: 'employer',
        amount: 2750,
        targetDebt: 'Tuition Assistance',
        rationale: 'Invest in career advancement for long-term earning potential.',
      },
    ],
    reasoning:
      'A balanced approach maximizes both debt reduction and career growth given your current situation.',
    projectedOutcome: {
      outOfPocketMonthly: 285,
      yearsToPayoff: 8.5,
      totalForgiven: 0,
      retirementContribution: 0,
    },
  };
}

export function getPslfVaultForClient(): PslfVaultSummary {
  const clientId = getCurrentDemoClientId() as string;

  const baseProgress: PslfProgress = {
    qualifyingPayments: 48,
    paymentsRemaining: 72,
    estimatedForgivenessDate: '2032-01-15',
    currentEmployerQualifies: true,
    currentEmployerName: 'Regional Medical Center',
    currentEmployerStartDate: '2022-01-15',
    repaymentPlan: 'SAVE (transitioning to RAP)',
    planQualifies: true,
    loanType: 'Direct Loans',
    loansQualify: true,
    monthlyPayment: 285,
    projectedForgivenessAmount: 68500,
    lastCertificationDate: '2025-10-15',
    nextCertificationDue: '2026-10-15',
    riskFactors: [
      {
        id: 'risk-1',
        severity: 'medium',
        title: 'Repayment Plan Transition',
        description: 'SAVE is transitioning to RAP in 2026. Your payment may increase.',
        action: 'Review new RAP payment estimate',
        // Consumed as a route NAME by pslf-vault's `<LinkTo @route=...>` — a URL
        // path here throws "There is no route named ..." and crashes the page.
        actionUrl: 'authenticated.reimagined.plan.strategy',
      },
    ],
    milestones: [
      { payments: 12, label: '1 Year', reached: true, reachedDate: '2023-01-15' },
      { payments: 24, label: '2 Years', reached: true, reachedDate: '2024-01-15' },
      { payments: 36, label: '3 Years', reached: true, reachedDate: '2025-01-15' },
      { payments: 48, label: '4 Years', reached: true, reachedDate: '2026-01-15' },
      { payments: 60, label: 'Halfway!', reached: false, reachedDate: null },
      { payments: 84, label: '7 Years', reached: false, reachedDate: null },
      { payments: 120, label: 'Forgiveness!', reached: false, reachedDate: null },
    ],
  };

  const baseDocuments: PslfVaultDocument[] = [
    {
      id: 'doc-1',
      formType: 'ecf',
      filename: 'ECF-2025-RegionalMedical.pdf',
      uploadedAt: '2025-10-20T14:30:00Z',
      certifiedDate: '2025-11-05T09:00:00Z',
      employerName: 'Regional Medical Center',
      coversPeriod: { start: '2024-10-01', end: '2025-09-30' },
      paymentsCertified: 12,
      status: 'certified',
      rejectionReason: null,
      downloadUrl: '#',
    },
    {
      id: 'doc-2',
      formType: 'ecf',
      filename: 'ECF-2024-RegionalMedical.pdf',
      uploadedAt: '2024-10-15T10:00:00Z',
      certifiedDate: '2024-11-01T09:00:00Z',
      employerName: 'Regional Medical Center',
      coversPeriod: { start: '2023-10-01', end: '2024-09-30' },
      paymentsCertified: 12,
      status: 'certified',
      rejectionReason: null,
      downloadUrl: '#',
    },
    {
      id: 'doc-3',
      formType: 'idr-application',
      filename: 'IDR-Recertification-2025.pdf',
      uploadedAt: '2025-08-01T08:00:00Z',
      certifiedDate: '2025-08-15T09:00:00Z',
      employerName: 'N/A',
      coversPeriod: { start: '2025-08-01', end: '2026-07-31' },
      paymentsCertified: 0,
      status: 'certified',
      rejectionReason: null,
      downloadUrl: '#',
    },
  ];

  if (clientId === 'legal') {
    return {
      totalDocuments: 5,
      certifiedPeriods: 4,
      pendingCertifications: 0,
      lastUpload: '2025-12-01T10:00:00Z',
      documents: [
        ...baseDocuments,
        {
          id: 'doc-4',
          formType: 'ecf',
          filename: 'ECF-2023-PublicDefender.pdf',
          uploadedAt: '2023-10-10T14:00:00Z',
          certifiedDate: '2023-10-25T09:00:00Z',
          employerName: 'Public Defender Office',
          coversPeriod: { start: '2022-10-01', end: '2023-09-30' },
          paymentsCertified: 12,
          status: 'certified',
          rejectionReason: null,
          downloadUrl: '#',
        },
      ],
      progress: {
        ...baseProgress,
        qualifyingPayments: 52,
        paymentsRemaining: 68,
        currentEmployerName: 'Public Defender Office',
        projectedForgivenessAmount: 82000,
      },
      complianceScore: 92,
      complianceGaps: ['Annual ECF due in 10 months'],
    };
  }

  return {
    totalDocuments: 3,
    certifiedPeriods: 2,
    pendingCertifications: 0,
    lastUpload: '2025-10-20T14:30:00Z',
    documents: baseDocuments,
    progress: baseProgress,
    complianceScore: 78,
    complianceGaps: [
      'Missing ECF for employment period 2022-01 to 2022-09',
      'Consider uploading employer verification letter as backup',
    ],
  };
}

export function getPslfJourneyForClient(): PslfJourney {
  const clientId = getCurrentDemoClientId() as string;
  const vault = getPslfVaultForClient();

  // Generate payment validations based on qualifying payments
  const generatePaymentValidations = (count: number, startDate: Date): PslfPaymentValidation[] => {
    const payments: PslfPaymentValidation[] = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < Math.min(count, 12); i++) {
      const paymentDate = new Date(currentDate);
      paymentDate.setMonth(paymentDate.getMonth() - i);

      payments.push({
        id: `payment-${count - i}`,
        paymentDate: paymentDate.toISOString().split('T')[0] as string,
        amount: 285,
        paymentNumber: count - i,
        status: 'qualifying',
        qualificationChecks: [
          {
            name: 'Payment Amount',
            passed: true,
            reason: 'Full payment received on time',
            category: 'amount',
          },
          {
            name: 'Payment Timing',
            passed: true,
            reason: 'Paid within billing cycle',
            category: 'timing',
          },
          {
            name: 'Repayment Plan',
            passed: true,
            reason: 'SAVE plan qualifies for PSLF',
            category: 'plan',
          },
          {
            name: 'Employer Status',
            passed: true,
            reason: '501(c)(3) nonprofit verified',
            category: 'employer',
          },
          {
            name: 'Employment Status',
            passed: true,
            reason: '40+ hours/week confirmed',
            category: 'employment',
          },
        ],
        servicerConfirmationId: `MOHELA-${Date.now()}-${i}`,
        validatedAt: paymentDate.toISOString(),
        validationSource: 'mohela_api',
      });
    }

    return payments;
  };

  // Generate achievements based on payment count
  const generateAchievements = (qualifyingPayments: number): PslfAchievement[] => {
    const milestoneConfig = [
      { type: 'started' as const, payments: 1, badge: 'Journey Begins', emoji: '🚀' },
      { type: 'first_year' as const, payments: 12, badge: 'First Year Champion', emoji: '⭐' },
      { type: 'two_years' as const, payments: 24, badge: 'Committed Servant', emoji: '🏅' },
      { type: 'four_years' as const, payments: 48, badge: 'Steadfast Hero', emoji: '💪' },
      { type: 'halfway' as const, payments: 60, badge: 'Halfway Hero', emoji: '🏆' },
      { type: 'six_years' as const, payments: 72, badge: 'Perseverance Pro', emoji: '🌟' },
      { type: 'eight_years' as const, payments: 96, badge: 'Almost There', emoji: '🎯' },
      { type: 'home_stretch' as const, payments: 108, badge: 'Home Stretch', emoji: '🏃' },
      { type: 'final_countdown' as const, payments: 114, badge: 'Final Countdown', emoji: '⏰' },
      { type: 'forgiveness' as const, payments: 120, badge: 'Freedom!', emoji: '🎉' },
    ];

    return milestoneConfig.map((config) => {
      const reached = qualifyingPayments >= config.payments;
      const achievedDate = reached
        ? new Date(Date.now() - (qualifyingPayments - config.payments) * 30 * 24 * 60 * 60 * 1000)
        : null;

      return {
        id: `achievement-${config.type}`,
        milestoneType: config.type,
        payments: config.payments,
        achievedAt: achievedDate?.toISOString() ?? null,
        badgeName: config.badge,
        badgeEmoji: config.emoji,
        message: reached
          ? `You've completed ${config.payments} qualifying payments!`
          : `${config.payments - qualifyingPayments} payments until "${config.badge}"`,
        statsAtMilestone: reached
          ? {
              totalPaid: config.payments * 285,
              forgivenessEarned: Math.round(
                (config.payments / 120) * vault.progress.projectedForgivenessAmount
              ),
              percentComplete: Math.round((config.payments / 120) * 100),
            }
          : null,
        shared: false,
      };
    });
  };

  const buildProjection = (progress: PslfProgress): PslfProjection => {
    const monthlyRate = progress.projectedForgivenessAmount / 120;
    return {
      qualifyingPaymentsToDate: progress.qualifyingPayments,
      paymentsRemaining: progress.paymentsRemaining,
      loanBalanceAtForgiveness: progress.projectedForgivenessAmount,
      projectedForgivenessAmount: progress.projectedForgivenessAmount,
      projectedForgivenessDate: progress.estimatedForgivenessDate,
      totalPaidToDate: progress.qualifyingPayments * progress.monthlyPayment,
      totalWillPay: 120 * progress.monthlyPayment,
      forgivenessValue: progress.projectedForgivenessAmount + 120 * progress.monthlyPayment,
      monthlyForgivenessRate: monthlyRate,
      yearlyForgivenessRate: monthlyRate * 12,
      earnedToDate: Math.round(
        (progress.qualifyingPayments / 120) * progress.projectedForgivenessAmount
      ),
    };
  };

  const buildAutomationStatus = (progress: PslfProgress): PslfAutomationStatus => {
    return {
      ecfStatus: 'automated',
      ecfStatusLabel: 'AUTOMATED',
      ecfDetails: 'Your employer HR auto-signs via Tuition.io',
      ecfLastSubmitted: progress.lastCertificationDate,
      ecfNextDue: progress.nextCertificationDue,
      idrStatus: 'easy_mode',
      idrStatusLabel: 'EASY MODE ON',
      idrDetails: 'Income pulled from IRS automatically for 1-click recert',
      idrPlanExpires: '2026-08-01',
      syncStatus: 'synced',
      syncStatusLabel: 'SYNCED',
      syncDetails: 'All payments verified and matching with MOHELA',
      syncLastUpdated: new Date().toISOString(),
      policyStatus: 'protected',
      policyStatusLabel: 'PROTECTED',
      policyDetails: 'Your grandfathered status is secure',
    };
  };

  const buildProtectionStatus = (): PslfProtectionStatus => {
    return {
      threatLevel: 'low',
      grandfathered: true,
      grandfatheredSince: '2022-01-15',
      protections: [
        'Enrolled before 2026: Grandfathered under current rules',
        'Direct Loans only: Not affected by FFEL restrictions',
        'Qualifying plan: SAVE/RAP plan is congressionally protected',
        'Qualifying employer: 501(c)(3) status verified',
        'Documentation: 100% of employment history certified',
      ],
      vulnerabilities: [],
      policyUpdates: [
        {
          id: 'update-1',
          date: '2026-02-01',
          title: 'HR 1234: PSLF Expansion Act introduced',
          description: 'Bill would expand PSLF to include more employer types',
          impactOnUser: 'positive',
          impactExplanation:
            'If passed, more employers would qualify - good for future job changes',
          source: 'Congress.gov',
          sourceUrl: 'https://congress.gov/bill/119/hr/1234',
        },
        {
          id: 'update-2',
          date: '2026-01-15',
          title: 'Project 2025 PSLF proposal analysis',
          description: 'Analysis of proposed changes to PSLF program',
          impactOnUser: 'none',
          impactExplanation: 'Grandfathered borrowers would be exempt under all proposals',
          source: 'ED.gov',
          sourceUrl: 'https://studentaid.gov/pslf',
        },
      ],
    };
  };

  const buildScenarios = (projection: PslfProjection): PslfScenario[] => {
    return [
      {
        type: 'leave_public_service',
        title: 'Leave Public Service',
        description: 'What happens if you take a private sector job?',
        impact: {
          forgivenessLost: projection.projectedForgivenessAmount,
          additionalCost:
            projection.projectedForgivenessAmount + projection.paymentsRemaining * 285,
          timeImpact: `Lose ${projection.qualifyingPaymentsToDate} months of progress`,
        },
        recommendation: 'do_not',
        message: `Leaving now would cost you $${projection.projectedForgivenessAmount.toLocaleString()} in forgiveness.`,
        details: [
          `You've already earned $${projection.earnedToDate.toLocaleString()} toward forgiveness`,
          `Only ${projection.paymentsRemaining} payments remaining (${Math.round(projection.paymentsRemaining / 12)} years)`,
          'Consider staying until forgiveness, then transition to private sector',
        ],
      },
      {
        type: 'refinance',
        title: 'Refinance to Private Lender',
        description: 'What happens if you refinance your federal loans?',
        impact: {
          forgivenessLost: projection.projectedForgivenessAmount,
          additionalCost: projection.projectedForgivenessAmount * 1.15, // Plus interest
          timeImpact: 'All 120 payments start over (not allowed with private loans)',
        },
        recommendation: 'do_not',
        message: '🚨 CRITICAL: Never refinance while pursuing PSLF!',
        details: [
          'Refinanced loans become private and are PERMANENTLY ineligible for PSLF',
          'You would lose ALL qualifying payment progress',
          `This would cost you $${(projection.projectedForgivenessAmount * 1.15).toLocaleString()}+`,
          'There is NO way to undo a refinance',
        ],
      },
      {
        type: 'change_employer',
        title: 'Change to Different Qualifying Employer',
        description: 'What happens if you change jobs within public service?',
        impact: {
          forgivenessLost: 0,
          additionalCost: 0,
          timeImpact: 'No impact if new employer qualifies',
        },
        recommendation: 'safe',
        message: 'Changing to another qualifying employer is safe!',
        details: [
          'Submit ECF for current employer before leaving',
          'Verify new employer qualifies (501(c)(3), government, etc.)',
          'Submit new ECF within 30 days of starting',
          'Your payment count continues uninterrupted',
        ],
      },
      {
        type: 'change_plan',
        title: 'Change Repayment Plan',
        description: 'What happens if you switch from your current IDR plan?',
        impact: {
          forgivenessLost: 0,
          additionalCost: 0,
          timeImpact: "Varies by plan - some don't qualify",
        },
        recommendation: 'caution',
        message: 'Only switch to another qualifying IDR plan',
        details: [
          '✅ SAVE, PAYE, IBR, ICR - All qualify for PSLF',
          '❌ Standard 10-year, Graduated, Extended - Do NOT qualify',
          'Switching between qualifying IDR plans is safe',
          'Consult our Strategy Finder before switching',
        ],
      },
    ];
  };

  const projection = buildProjection(vault.progress);
  const achievements = generateAchievements(vault.progress.qualifyingPayments);
  const nextAchievement = achievements.find((a) => !a.achievedAt) ?? null;

  // Legal scenario - further along
  if (clientId === 'legal') {
    const legalProgress = { ...vault.progress, qualifyingPayments: 52, paymentsRemaining: 68 };
    const legalProjection = buildProjection(legalProgress);
    const legalAchievements = generateAchievements(52);

    return {
      progress: legalProgress,
      projection: legalProjection,
      recentPayments: generatePaymentValidations(52, new Date()),
      allPaymentsCount: { qualifying: 52, pending: 0, nonQualifying: 0 },
      automationStatus: buildAutomationStatus(legalProgress),
      protectionStatus: buildProtectionStatus(),
      achievements: legalAchievements,
      nextAchievement: legalAchievements.find((a) => !a.achievedAt) ?? null,
      scenarios: buildScenarios(legalProjection),
      vault,
    };
  }

  // Healthcare/default scenario
  return {
    progress: vault.progress,
    projection,
    recentPayments: generatePaymentValidations(vault.progress.qualifyingPayments, new Date()),
    allPaymentsCount: {
      qualifying: vault.progress.qualifyingPayments,
      pending: 0,
      nonQualifying: 0,
    },
    automationStatus: buildAutomationStatus(vault.progress),
    protectionStatus: buildProtectionStatus(),
    achievements,
    nextAchievement,
    scenarios: buildScenarios(projection),
    vault,
  };
}
